/**
 * Per-party monthly income and tax figures, read out of the calculated Worksheet 1.
 *
 * Extracted verbatim from `BudgetPage.tsx` so the Budget page and the Alimony Analysis step
 * (docx §4) cannot drift apart. `BudgetPage.tsx` is the only prior implementation; both now call
 * this. No React here so it can be covered directly by vitest.
 *
 * IMPORTANT — these figures are NOT "before alimony". `sourcesAndAmountsSection.total` already
 * includes inter-party spousal support (`worksheet1Calcs.ts`:
 * `total = sourcesAndAmounts + spousalSupportReceivedYearly - |spousalSupportPaidYearly|`).
 * A caller that needs a pre-alimony baseline must recalculate against a case with the inter-party
 * entries removed first — see `alimonyAnalysis.ts` `computeBaseline`.
 */

import type { CaseData } from '../types/case';
import { computeNetIncomeBreakdown, type PartyIncomeBreakdown } from './netIncomeBreakdown';

export interface PartyMonthlyIncome {
    /** Gross annual income / 12. */
    grossMonthly: number;
    /** Negative (a liability), matching the worksheet's sign convention. */
    federalTax: number;
    /** Negative. */
    stateTax: number;
    /** Negative. */
    socialSecurity: number;
    /** grossMonthly + the three (already-negative) tax figures. */
    netMonthly: number;
    /** Every figure behind these, itemised, for showing the working. Absent on hand-built values. */
    breakdown?: PartyIncomeBreakdown;
}

export interface NetMonthlyIncomeResult {
    partyA: PartyMonthlyIncome;
    partyB: PartyMonthlyIncome;
}

const ZERO: PartyMonthlyIncome = {
    grossMonthly: 0,
    federalTax: 0,
    stateTax: 0,
    socialSecurity: 0,
    netMonthly: 0,
};

/**
 * For split custody the main case's worksheet1 is undefined; income lives in splitCases[0].
 * Income is per-party, so the first split case is correct for any party-level display.
 */
function resolveWorksheetCase(caseData: CaseData | null | undefined): CaseData | null | undefined {
    if (caseData?.documents?.worksheet1) return caseData;
    return caseData?.documents?.splitCases?.[0] ?? caseData;
}

/**
 * Reads gross, the three final tax liabilities and net monthly income for both parties.
 *
 * Returns all-zero figures when the case has no calculated worksheet1 (the same fallback
 * BudgetPage used).
 */
export function computeNetMonthlyIncome(caseData: CaseData | null | undefined): NetMonthlyIncomeResult {
    const worksheetCase = resolveWorksheetCase(caseData);
    const worksheet1 = worksheetCase?.documents?.worksheet1;

    if (!worksheet1) {
        return { partyA: { ...ZERO }, partyB: { ...ZERO } };
    }

    const petitionerWorksheet = worksheet1.petitioner;
    const respondentWorksheet = worksheet1.respondent;

    let petitionerGrossAnnual = petitionerWorksheet?.sourcesAndAmountsSection?.total || 0.0;
    let respondentGrossAnnual = respondentWorksheet?.sourcesAndAmountsSection?.total || 0.0;

    let petitionerFederalTaxAnnual = petitionerWorksheet?.federalTaxDeductionSection?.finalTaxLiability || 0.0;
    let respondentFederalTaxAnnual = respondentWorksheet?.federalTaxDeductionSection?.finalTaxLiability || 0.0;
    let petitionerStateTaxAnnual = petitionerWorksheet?.stateTaxDeductionSection?.finalTaxLiability || 0.0;
    let respondentStateTaxAnnual = respondentWorksheet?.stateTaxDeductionSection?.finalTaxLiability || 0.0;
    let petitionerSocialSecurityAnnual = petitionerWorksheet?.socialSecurityPensionDeductionSection?.annualSocialSecurityAndMedicareTaxLiability || 0.0;
    let respondentSocialSecurityAnnual = respondentWorksheet?.socialSecurityPensionDeductionSection?.annualSocialSecurityAndMedicareTaxLiability || 0.0;

    // Alternating tax schedule: average each even-year annual value with the main case's before
    // dividing by 12, so the monthly figure reflects a typical year rather than the odd year alone.
    if (caseData?.children?.taxSchedule === 'alternating') {
        const evenCase = worksheetCase?.documents?.alternatingEvenCase;
        if (evenCase != null) {
            const pEvenWorksheet = evenCase?.documents?.worksheet1?.petitioner;
            const rEvenWorksheet = evenCase?.documents?.worksheet1?.respondent;

            petitionerGrossAnnual = (petitionerGrossAnnual + (pEvenWorksheet?.sourcesAndAmountsSection?.total || 0)) / 2.0;
            respondentGrossAnnual = (respondentGrossAnnual + (rEvenWorksheet?.sourcesAndAmountsSection?.total || 0)) / 2.0;

            petitionerFederalTaxAnnual = (petitionerFederalTaxAnnual + (pEvenWorksheet?.federalTaxDeductionSection?.finalTaxLiability || 0)) / 2.0;
            respondentFederalTaxAnnual = (respondentFederalTaxAnnual + (rEvenWorksheet?.federalTaxDeductionSection?.finalTaxLiability || 0)) / 2.0;

            petitionerStateTaxAnnual = (petitionerStateTaxAnnual + (pEvenWorksheet?.stateTaxDeductionSection?.finalTaxLiability || 0)) / 2.0;
            respondentStateTaxAnnual = (respondentStateTaxAnnual + (rEvenWorksheet?.stateTaxDeductionSection?.finalTaxLiability || 0)) / 2.0;

            petitionerSocialSecurityAnnual = (petitionerSocialSecurityAnnual + (pEvenWorksheet?.socialSecurityPensionDeductionSection?.annualSocialSecurityAndMedicareTaxLiability || 0)) / 2.0;
            respondentSocialSecurityAnnual = (respondentSocialSecurityAnnual + (rEvenWorksheet?.socialSecurityPensionDeductionSection?.annualSocialSecurityAndMedicareTaxLiability || 0)) / 2.0;
        }
    }

    const petitioner = monthlyFromAnnual(
        petitionerGrossAnnual, petitionerFederalTaxAnnual, petitionerStateTaxAnnual, petitionerSocialSecurityAnnual);
    const respondent = monthlyFromAnnual(
        respondentGrossAnnual, respondentFederalTaxAnnual, respondentStateTaxAnnual, respondentSocialSecurityAnnual);

    const isPartyAPetitioner = caseData?.representation?.petitionerParty === 'partyA';

    const partyA = isPartyAPetitioner ? petitioner : respondent;
    const partyB = isPartyAPetitioner ? respondent : petitioner;
    return {
        partyA: { ...partyA, breakdown: computeNetIncomeBreakdown(caseData, 'partyA', partyA) },
        partyB: { ...partyB, breakdown: computeNetIncomeBreakdown(caseData, 'partyB', partyB) },
    };
}

function monthlyFromAnnual(
    grossAnnual: number,
    federalAnnual: number,
    stateAnnual: number,
    socialSecurityAnnual: number,
): PartyMonthlyIncome {
    const grossMonthly = grossAnnual / 12.0;
    const federalTax = federalAnnual / 12.0;
    const stateTax = stateAnnual / 12.0;
    const socialSecurity = socialSecurityAnnual / 12.0;

    return {
        grossMonthly,
        federalTax,
        stateTax,
        socialSecurity,
        // The three tax figures are already negative, so this is a sum, not a subtraction.
        netMonthly: grossMonthly + federalTax + stateTax + socialSecurity,
    };
}
